import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  Paper,
  Tab,
  Tabs,
  Typography
} from "@mui/material";
import { t } from "ttag";
import { useStoreActions } from "../../models/RootStore";
import ServiceNavigationBar from "../../widgets/Menu/ServiceNavigationBar";
import DeviceInformationCard from "../DeviceManagment/DeviceInformationCard";
import {
  manageDeviceSyncEntity,
  validateDeviceSync,
  validateDeviceSyncEntity
} from "./deviceSyncApi";
import {
  formatBatchSyncOutcome,
  formatSyncError,
  formatSyncOutcome,
  isMongoDeviceId
} from "./deviceSyncUtils";
import {
  canCompareEntity,
  entityHasMissingInCloud,
  entityHasMissingOnDevice,
  entityStatusIgnoringTimestamps,
  resolveEntityStatus
} from "./deviceSyncCompare";
import {
  ALL_SYNC_ENTITIES,
  collectDirectionSyncJobs,
  collectSuggestedSyncJobs,
  entitiesForValidate,
  getEntityLabel,
  getSyncTabs,
  getUnsupportedSyncReason,
  isDeviceConnected,
  isNgDevice,
  SyncAction,
  SyncJob,
  tabIndexForEntity
} from "./deviceSyncConfig";
import JsonCompare from "./JsonCompare";
import SyncConfirmDialog from "./SyncConfirmDialog";
import SyncEntityPanel from "./SyncEntityPanel";
import SyncEntityTable from "./SyncEntityTable";
import SyncToolbar from "./SyncToolbar";
import { statusColor } from "./SyncStatusIndicators";
import useStyles from "./DeviceSync.style";

const POLL_MS = 60 * 1000;

const pickDevice = (payload: any): any => {
  if (!payload) {
    return null;
  }
  if (payload.id || payload._id) {
    return payload;
  }
  const values = Object.values(payload);
  return values.find((item: any) => item && (item.id || item._id)) || values[0] || null;
};

const DeviceSync = (props: any) => {
  const classes = useStyles();
  const deviceParam = props.match?.params?.deviceId || "";
  const getDeviceById = useStoreActions((actions) => actions.devices.getDeviceById);
  const getDevicesBySerial = useStoreActions((actions) => actions.devices.getDevicesBySerial);
  const { addMessage } = useStoreActions((actions) => actions.errorMessage);

  const [device, setDevice] = useState<any>(null);
  const [deviceError, setDeviceError] = useState("");
  const [loadingDevice, setLoadingDevice] = useState(true);
  const [entities, setEntities] = useState<Record<string, any>>({});
  const [loadingCompare, setLoadingCompare] = useState(false);
  const [tab, setTab] = useState(0);
  const [confirm, setConfirm] = useState<SyncJob[] | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [refreshingEntity, setRefreshingEntity] = useState<string | null>(null);

  const inFlightRef = useRef(false);
  const connectedRef = useRef(false);
  const pageScrollRef = useRef(0);
  const loadCompareRef = useRef<(...args: any[]) => Promise<void>>(async () => undefined);
  const refreshDeviceStatusRef = useRef<(id: string) => Promise<void>>(async () => undefined);
  const deviceId = device?.id || device?._id || "";
  connectedRef.current = isDeviceConnected(device);

  const showMessage = (message: string) => {
    if (message) {
      addMessage({ message });
    }
  };

  const loadCompare = useCallback(async (
    id: string,
    silent = false,
    includeTemplates = false
  ) => {
    if (!id || inFlightRef.current) {
      return;
    }
    inFlightRef.current = true;
    if (!silent) {
      setLoadingCompare(true);
    }
    try {
      const data = await validateDeviceSync(id, entitiesForValidate(includeTemplates));
      setEntities((current) => ({ ...current, ...(data?.entities || {}) }));

      const failed = Object.values(data?.entities || {}).filter((item: any) => item?.error);
      if (!silent && failed.length > 0) {
        const first: any = failed[0];
        const label = getEntityLabel(first.entity);
        addMessage({
          message: formatSyncError(first.error, t`${label} compare failed.`)
        });
      }
      if (!silent && Array.isArray(data?.errors) && data.errors.length > 0 && failed.length === 0) {
        addMessage({ message: data.errors[0] });
      }
    } catch (error) {
      if (!silent) {
        addMessage({
          message: formatSyncError(error, t`Couldn't compare cloud and device. Try again.`)
        });
      }
    } finally {
      inFlightRef.current = false;
      setLoadingCompare(false);
    }
  }, [addMessage]);
  loadCompareRef.current = loadCompare;

  const refreshEntity = useCallback(async (entity: string) => {
    if (!deviceId || !entity) {
      return;
    }
    setRefreshingEntity(entity);
    try {
      const data = await validateDeviceSyncEntity(deviceId, entity);
      setEntities((current) => ({ ...current, [entity]: data }));
      if (data?.error) {
        addMessage({
          message: formatSyncError(data.error, t`${getEntityLabel(entity)} compare failed.`)
        });
      }
    } catch (error) {
      addMessage({
        message: formatSyncError(error, t`Couldn't refresh ${getEntityLabel(entity)}. Try again.`)
      });
    } finally {
      setRefreshingEntity(null);
    }
  }, [deviceId, addMessage]);

  const refreshDeviceStatus = useCallback(async (id: string) => {
    if (!id) {
      return;
    }
    try {
      const nextDevice = pickDevice(await getDeviceById(id));
      if (!nextDevice) {
        return;
      }
      setDevice((current: any) => {
        if (!current) {
          return nextDevice;
        }
        if (
          current.isConnected === nextDevice.isConnected &&
          current.disconnectedTimestamp === nextDevice.disconnectedTimestamp
        ) {
          return current;
        }
        return {
          ...current,
          isConnected: nextDevice.isConnected,
          disconnectedTimestamp: nextDevice.disconnectedTimestamp
        };
      });
    } catch {
      // Keep the last known connection status.
    }
  }, [getDeviceById]);
  refreshDeviceStatusRef.current = refreshDeviceStatus;

  useEffect(() => {
    let cancelled = false;

    const resolveDevice = async () => {
      setLoadingDevice(true);
      setDeviceError("");
      try {
        let nextDevice = null;
        if (isMongoDeviceId(deviceParam)) {
          nextDevice = pickDevice(await getDeviceById(deviceParam));
        } else {
          const bySerial = await getDevicesBySerial({ serial: deviceParam });
          const list = Array.isArray(bySerial) ? bySerial : Object.values(bySerial || {});
          nextDevice = list.find((item: any) => item?.serial === deviceParam) || pickDevice(bySerial);
        }

        if (cancelled) {
          return;
        }
        if (!nextDevice) {
          setDevice(null);
          setDeviceError(t`Device not found.`);
          addMessage({ message: t`Device not found.` });
          return;
        }
        if (!isNgDevice(nextDevice)) {
          setDevice(null);
          setDeviceError(t`This page is for NG devices only.`);
          addMessage({ message: t`This page is for NG devices only.` });
          return;
        }
        setDevice(nextDevice);
      } catch (error) {
        if (!cancelled) {
          const message = formatSyncError(error, t`Couldn't load this device.`);
          setDeviceError(message);
          addMessage({ message });
        }
      } finally {
        if (!cancelled) {
          setLoadingDevice(false);
        }
      }
    };

    resolveDevice();
    return () => {
      cancelled = true;
    };
  }, [deviceParam, getDeviceById, getDevicesBySerial, addMessage]);

  useEffect(() => {
    if (!deviceId || deviceError) {
      return undefined;
    }
    if (connectedRef.current) {
      loadCompareRef.current(deviceId);
    }
    refreshDeviceStatusRef.current(deviceId);
    const timer = window.setInterval(() => {
      if (connectedRef.current) {
        loadCompareRef.current(deviceId, true);
      }
      refreshDeviceStatusRef.current(deviceId);
    }, POLL_MS);
    return () => window.clearInterval(timer);
  }, [deviceId, deviceError]);

  useEffect(() => {
    if (!deviceId || !isDeviceConnected(device)) {
      return;
    }
    loadCompareRef.current(deviceId, true);
  }, [deviceId, device?.isConnected]);

  useEffect(() => {
    const scroller = document.querySelector('[class*="pageContent"]') as HTMLElement | null;
    if (!scroller) {
      return undefined;
    }
    const onScroll = () => {
      pageScrollRef.current = scroller.scrollTop;
    };
    pageScrollRef.current = scroller.scrollTop;
    scroller.addEventListener("scroll", onScroll, { passive: true });
    return () => scroller.removeEventListener("scroll", onScroll);
  }, [deviceId]);

  useLayoutEffect(() => {
    const scroller = document.querySelector('[class*="pageContent"]') as HTMLElement | null;
    if (scroller) {
      scroller.scrollTop = pageScrollRef.current;
    }
  });

  const syncTabs = getSyncTabs();
  const currentTab = syncTabs[tab] || syncTabs[0];
  const currentEntity = currentTab.entities[0];
  const currentResult = entities[currentEntity];

  const tabStatus = useMemo(() => {
    return getSyncTabs().map((item) => {
      const results = item.entities
        .map((entity) => entities[entity])
        .filter((result) => canCompareEntity(result));
      if (results.length === 0) {
        return "";
      }
      return results.some((result) => entityStatusIgnoringTimestamps(result) === "fail")
        ? "fail"
        : "pass";
    });
  }, [entities]);

  const suggestedJobs = useMemo(
    () =>
      loadingCompare
        ? []
        : collectSuggestedSyncJobs(
            ALL_SYNC_ENTITIES.map((entity) => {
              const result = entities[entity];
              return {
                entity,
                status: resolveEntityStatus(result),
                missingOnDevice: entityHasMissingOnDevice(result),
                missingInCloud: entityHasMissingInCloud(result)
              };
            })
          ),
    [entities, loadingCompare]
  );

  const deviceConnected = isDeviceConnected(device);

  const requestSync = (entity: string, action: SyncAction) => {
    if (!deviceConnected || getUnsupportedSyncReason(entity, action)) {
      return;
    }
    setConfirm([{ entity, action }]);
  };

  const requestSuggestedSync = () => {
    if (!deviceConnected || suggestedJobs.length === 0) {
      return;
    }
    setConfirm(suggestedJobs);
  };

  const requestAllSync = (action: SyncAction) => {
    const jobs = collectDirectionSyncJobs(action);
    if (!deviceConnected || jobs.length === 0) {
      return;
    }
    setConfirm(jobs);
  };

  const runSync = async () => {
    const jobs = (confirm || []).filter(
      (job) => !getUnsupportedSyncReason(job.entity, job.action)
    );
    if (!deviceId || !deviceConnected || jobs.length === 0) {
      setConfirm(null);
      return;
    }
    setSyncing(true);
    let ok = 0;
    let fail = 0;
    let lastError = "";
    try {
      for (const job of jobs) {
        const label = getEntityLabel(job.entity);
        try {
          const data = await manageDeviceSyncEntity(deviceId, job.entity, job.action);
          if (data?.success === false) {
            fail += 1;
            lastError = formatSyncError(data, formatSyncOutcome(label, job.action, false));
          } else {
            ok += 1;
          }
        } catch (error) {
          fail += 1;
          lastError = formatSyncError(error, formatSyncOutcome(label, job.action, false));
        }
      }
      setConfirm(null);
      if (jobs.length === 1) {
        const job = jobs[0];
        const label = getEntityLabel(job.entity);
        showMessage(
          fail
            ? lastError || formatSyncOutcome(label, job.action, false)
            : formatSyncOutcome(label, job.action, true)
        );
      } else {
        showMessage(formatBatchSyncOutcome(ok, fail));
      }
      await loadCompare(
        deviceId,
        false,
        currentEntity === "bacnetTemplates" || jobs.some((job) => job.entity === "bacnetTemplates")
      );
    } finally {
      setSyncing(false);
    }
  };

  return (
    <ServiceNavigationBar title={t`NG device sync`} {...props}>
      <Box className={classes.page}>
        {loadingDevice && (
          <Box display="flex" justifyContent="center" mt={6}>
            <CircularProgress />
          </Box>
        )}

        {!loadingDevice && deviceError && (
          <Typography className={classes.empty}>{deviceError}</Typography>
        )}

        {!loadingDevice && !deviceError && isNgDevice(device) && (
          <>
            <SyncToolbar
              deviceConnected={deviceConnected}
              loadingCompare={loadingCompare}
              syncing={syncing}
              suggestedCount={suggestedJobs.length}
              onFetch={() => {
                if (!deviceConnected) {
                  return;
                }
                loadCompare(deviceId, false, currentEntity === "bacnetTemplates");
                refreshDeviceStatus(deviceId);
              }}
              onPullAll={() => requestAllSync("pull")}
              onPushAll={() => requestAllSync("push")}
              onSmartSync={requestSuggestedSync}
            />

            <Box className={classes.topGrid}>
            <DeviceInformationCard
              device={device}
              setDevice={setDevice}
              deviceId={deviceId}
              history={props.history}
              companion="terminal"
              layout="row"
              compact
              onRetryConnection={() => refreshDeviceStatus(deviceId)}
            />

            <SyncEntityTable
              entities={entities}
              currentEntity={currentEntity}
              loadingCompare={loadingCompare}
              deviceConnected={deviceConnected}
              syncing={syncing}
              onSelectEntity={(entity) => setTab(tabIndexForEntity(entity).tab)}
              onRequestSync={requestSync}
            />
            </Box>

            <Tabs
              value={tab}
              onChange={(_, value) => setTab(value)}
              className={classes.tabsBar}
              variant="scrollable"
              scrollButtons="auto"
            >
              {syncTabs.map((item, index) => (
                <Tab
                  key={item.id}
                  label={
                    <span className={classes.tabLabel}>
                      {item.label}
                      {tabStatus[index] && (
                        <Chip
                          size="small"
                          color={statusColor(tabStatus[index])}
                          label={tabStatus[index]}
                        />
                      )}
                    </span>
                  }
                />
              ))}
            </Tabs>

            <Paper className={classes.section}>
              {loadingCompare && !currentResult ? (
                <Box display="flex" justifyContent="center" py={4}>
                  <CircularProgress />
                </Box>
              ) : (
                <SyncEntityPanel
                  entity={currentEntity}
                  result={currentResult}
                  loadingCompare={loadingCompare}
                  deviceConnected={deviceConnected}
                  syncing={syncing}
                  refreshing={refreshingEntity === currentEntity}
                  onRequestSync={requestSync}
                  onRefresh={refreshEntity}
                />
              )}
            </Paper>
            {(currentResult?.device || currentResult?.cloud) && (
              <Paper className={classes.section}>
                <JsonCompare
                  entity={currentEntity}
                  deviceSnapshot={currentResult?.device}
                  cloudSnapshot={currentResult?.cloud}
                />
              </Paper>
            )}
          </>
        )}

        <SyncConfirmDialog
          jobs={confirm}
          syncing={syncing}
          deviceConnected={deviceConnected}
          onCancel={() => setConfirm(null)}
          onConfirm={runSync}
        />
      </Box>
    </ServiceNavigationBar>
  );
};

export default DeviceSync;
